import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  Card,
  CardCommandType,
  CardVendorType,
  MatchType,
  SearchCriteria,
} from '@models/card/model';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs';

import { CoreService } from '../models/clux/enum';
import { CommandFactory } from '../utils/command-factory';
import { Uri } from '../utils/uri';

@Injectable({
  providedIn: 'root',
})
export class CardService {
  private commandFactory = inject(CommandFactory);
  private http = inject(HttpClient);

  /**
   * Return the cards belonging to the specified individual or dependents.
   * @param personIds The IDs of the individual or dependents
   */
  public getCards(personIds: string[]): Observable<Card[]> {
    if (!personIds || !personIds.length) {
      return of([]);
    }

    const criteria: SearchCriteria = [
      {
        key: 'parentId',
        value: personIds.join('|'),
        matchType: MatchType.IN,
      },
    ];

    const uri = new Uri('/profile/*/card/search', CoreService.Card);
    return this.http.post<Card[]>(uri.toString(), criteria);
  }

  public sortCardsForDisplay = (a: Card, b: Card): number => {
    // individual cards before dependent
    const byParentType = a.parentType > b.parentType ? -1 : a.parentType < b.parentType ? 1 : 0;
    if (byParentType !== 0) {
      return byParentType;
    }
    // cardholder alphabetically
    const byName = a.cardHolderName > b.cardHolderName ? 1 : a.cardHolderName < b.cardHolderName ? -1 : 0;
    if (byName !== 0) {
      return byName;
    }
    // physical cards before digital
    const byVendor = a.vendor !== b.vendor && a.vendor === CardVendorType.DIGITAL_ONLY ? 1 : a.vendor !== b.vendor && b.vendor === CardVendorType.DIGITAL_ONLY ? -1 : 0;
    if (byVendor !== 0) {
      return byVendor;
    }
    // issue date desc
    return a.issueDateTime > b.issueDateTime ? -1 : a.issueDateTime < b.issueDateTime ? 1 : 0;
  };

  public updateCard(card: Card, commandName: CardCommandType, useQueue: boolean = false): Observable<Card> {
    const command = this.commandFactory.createCommand(card, commandName);
    const uri = new Uri(`/profile/${card.individualId}/card/${card.id}/command/${command.type}`, CoreService.Card);
    const opts = useQueue ? { headers: { 'x-uba-route': 'queue' } } : undefined;
    return this.http.put<void>(uri.toString(), command, opts).pipe(map(() => command.data));
  }
}
